/**
 * Residual Codes: Manage Investors' Loan / Lease tagging of each residual code.
 *
 * One row per residual-value code (e.g. "$L", "FV"); `residualType` says whether that code marks a
 * contract a Loan or a Lease. It does not depend on the investor code. Targets
 * athena_input.residual_code_mapping, which is created and loaded outside this app.
 *
 * Governed, like Manage Investors: a reviewer proposes a change, a contributor approves or
 * rejects it, and every proposal stays in the change log. There is no direct write. See
 * portfolio-risk-concentrations/app/router/residual_codes.py's module docstring.
 *
 * The UI says Loans / Leases. The table holds what ingestion writes (Loan/EFA, Lease) and the
 * backend translates both ways, so nothing here needs to know the stored spelling.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { QueryClient } from "@tanstack/react-query";

import { getJson, postJson, PRC_PREFIX } from "./client";

export type ResidualType = "Loans" | "Leases";

export const RESIDUAL_TYPES: ResidualType[] = ["Loans", "Leases"];

export interface ResidualCode {
  /** Exactly as stored, including any leading or trailing space (" L" is a real code). */
  residualCode: string;
  /**
   * "Loans" or "Leases" as the row reads (an ingested Loan/EFA or Lease is translated). A stored value
   * that is neither is passed through as it is, so it is visible rather than hidden.
   */
  residualType: string | null;
  /** Exactly what the table holds. */
  storedType: string | null;
}

export interface ResidualCodeProposal {
  proposalId: string;
  residualCode: string;
  /** ADD: a code not in the mapping yet (approval inserts it). ADJUST: a change to an existing code's type. */
  changeType: "ADD" | "ADJUST";
  /** What the code read when the proposal was raised; null for an ADD. */
  oldResidualType: string | null;
  newResidualType: string | null;
  reason: string;
  proposedByRole: string;
  proposedAt: string | null;
  status: "PENDING" | "APPROVED" | "REJECTED";
  reviewedByRole: string | null;
  reviewedAt: string | null;
  reviewNotes: string | null;
}

export interface ResidualCodeReviewResult {
  ok: boolean;
  error?: string;
  decision?: "APPROVED" | "REJECTED";
  applied?: boolean;
  residualCode?: string;
  residualType?: string | null;
}

export function useResidualCodes(page: { limit: number; offset: number }) {
  return useQuery({
    queryKey: ["prc-residual-codes", page],
    queryFn: () => {
      const q = new URLSearchParams({ limit: String(page.limit), offset: String(page.offset) });
      return getJson<{ residualCodes: ResidualCode[]; total: number }>(
        `${PRC_PREFIX}residual-codes?${q.toString()}`,
      );
    },
  });
}

/** The change log (newest first); `status: "PENDING"` is the approval queue. */
export function useResidualCodeProposals(page: {
  limit: number;
  offset: number;
  status?: ResidualCodeProposal["status"];
}) {
  return useQuery({
    queryKey: ["prc-residual-code-proposals", page],
    queryFn: () => {
      const q = new URLSearchParams({ limit: String(page.limit), offset: String(page.offset) });
      if (page.status) q.set("status", page.status);
      return getJson<{ proposals: ResidualCodeProposal[]; total: number }>(
        `${PRC_PREFIX}residual-code-proposals?${q.toString()}`,
      );
    },
  });
}

function invalidateResidualCodes(qc: QueryClient) {
  qc.invalidateQueries({ queryKey: ["prc-residual-codes"] });
  qc.invalidateQueries({ queryKey: ["prc-residual-code-proposals"] });
}

export function useProposeResidualCode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      residualCode: string;
      residualType: ResidualType;
      reason: string;
      /** Stated by the screen so a typo cannot quietly turn a change into a new code (or the reverse). */
      changeType?: "ADD" | "ADJUST";
    }) => postJson<{ proposalId: string; changeType: "ADD" | "ADJUST" }>(`${PRC_PREFIX}residual-code-proposals`, input),
    onSuccess: () => invalidateResidualCodes(qc),
  });
}

export function useReviewResidualCode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      proposalId: string;
      decision: "APPROVED" | "REJECTED";
      notes: string;
    }) =>
      postJson<ResidualCodeReviewResult>(
        `${PRC_PREFIX}residual-code-proposals/${encodeURIComponent(input.proposalId)}/review`,
        { decision: input.decision, notes: input.notes },
      ),
    onSuccess: () => invalidateResidualCodes(qc),
  });
}
